import { useEffect, useState } from 'react';
import { opsApi, getErrorMessage } from '../lib/api';

interface TriggerResult {
  success: boolean;
  message: string;
  data?: unknown;
}

export default function Operations() {
  const [results, setResults] = useState<Record<string, TriggerResult>>({});
  const [loading, setLoading] = useState<string | null>(null);

  const runTrigger = async (
    key: string,
    label: string,
    fn: () => Promise<unknown>,
  ) => {
    setLoading(key);
    try {
      const data = await fn();
      setResults((prev) => ({
        ...prev,
        [key]: { success: true, message: `${label} completed successfully`, data },
      }));
    } catch (err) {
      setResults((prev) => ({
        ...prev,
        [key]: { success: false, message: getErrorMessage(err) },
      }));
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Operations</h2>

      <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-4">
        <p className="text-sm text-yellow-200">
          These operations run on the public API Worker and may take several seconds.
          They manipulate the Worker's own state (KV, R2, edge cache) and cannot be
          run from the admin Worker.
        </p>
      </div>

      <div className="space-y-4">
        {/* Leaderboard rebuild */}
        <OpsCard
          title="Rebuild Leaderboards"
          description="Discovers all scopes from D1, sets dirty flags for all scope × period × metric combinations, then runs the compiler. This regenerates all leaderboard snapshots in R2."
          buttonText="Rebuild All Leaderboards"
          loading={loading === 'leaderboard'}
          result={results.leaderboard}
          onClick={() => runTrigger('leaderboard', 'Leaderboard rebuild', () => opsApi.rebuildLeaderboards())}
        />

        {/* Profanity scan */}
        <OpsCard
          title="Profile Profanity Scan"
          description="Runs the daily profile profanity cron. Scans user profiles (nicknames, bios) for profanity using OpenAI. Use 'Full Scan' to check all profiles (not just recently updated ones)."
          buttonText="Run Profanity Scan"
          buttonTextSecondary="Full Scan"
          loading={loading === 'profanity'}
          result={results.profanity}
          onClick={() => runTrigger('profanity', 'Profanity scan', () => opsApi.triggerProfanityScan(false))}
          onClickSecondary={() => runTrigger('profanity', 'Full profanity scan', () => opsApi.triggerProfanityScan(true))}
        />

        {/* Edge cache reset */}
        <PurgeCacheCard />

        {/* AI moderation */}
        <OpsCard
          title="AI Moderation Review"
          description="Reviews pending reported UGC submissions using OpenAI and auto-hides content that exceeds the report threshold. Purges the edge cache for affected venues."
          buttonText="Run AI Moderation"
          loading={loading === 'moderation'}
          result={results.moderation}
          onClick={() => runTrigger('moderation', 'AI moderation', () => opsApi.triggerAiModeration())}
        />
      </div>
    </div>
  );
}

/** The only path family the Worker accepts for a scoped purge. Mirrors PURGE_PATH_PREFIX_ROOT. */
const PURGE_PREFIX_ROOT = '/v1/nearby/';

/**
 * Edge cache reset.
 *
 * This is the one destructive operation on the page, so it is deliberately harder to fire than the
 * others: the button arms on the first click and executes on the second, disarming itself after a
 * few seconds. A stray click must not cold-start the edge cache for a whole city (or, in
 * `everything` mode, for every city at once).
 *
 * Scoped mode exists because the tile path is `/v1/nearby/{gh6}`, so a prefix like `/v1/nearby/sw`
 * targets exactly one city's cells. Seeding İzmir therefore does not re-hammer İstanbul's dense
 * cells.
 */
function PurgeCacheCard() {
  const [mode, setMode] = useState<'everything' | 'prefix'>('prefix');
  const [prefix, setPrefix] = useState(PURGE_PREFIX_ROOT);
  const [armed, setArmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TriggerResult | null>(null);

  const prefixError =
    mode === 'prefix' && !prefix.trim().startsWith(PURGE_PREFIX_ROOT)
      ? `Prefix must start with ${PURGE_PREFIX_ROOT}`
      : '';

  // Disarm after a few seconds so an armed button cannot sit there waiting for a stray click.
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(t);
  }, [armed]);

  const run = async () => {
    if (!armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    setLoading(true);
    setResult(null);
    try {
      const data = await opsApi.purgeCache(
        mode === 'everything' ? { purgeEverything: true } : { pathPrefixes: [prefix.trim()] },
      );
      setResult({
        success: data.success,
        message: data.success ? 'Cache purged' : 'Purge reported failure — check the Worker logs',
        data,
      });
    } catch (err) {
      setResult({ success: false, message: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-900 border border-red-900/50 rounded-lg p-4">
      <h3 className="text-lg font-semibold">Purge Edge Cache</h3>
      <p className="text-sm text-gray-400 mt-1 mb-3">
        Invalidates cached responses at the edge. Per-request freshness is handled by the version
        counter, so this is only needed for bulk events: a city seed, an import, or a data repair.
      </p>

      <div className="flex flex-col gap-2 mb-3 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="purge-mode"
            checked={mode === 'prefix'}
            onChange={() => setMode('prefix')}
          />
          Geohash prefix (scoped to one city)
        </label>
        <input
          aria-label="Geohash prefix"
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm disabled:opacity-50"
          value={prefix}
          disabled={mode !== 'prefix'}
          onChange={(e) => setPrefix(e.target.value)}
          placeholder="/v1/nearby/sw"
        />
        {prefixError && <span className="text-red-400 text-xs">{prefixError}</span>}
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="purge-mode"
            checked={mode === 'everything'}
            onChange={() => setMode('everything')}
          />
          Everything (all cities)
        </label>
      </div>

      <button
        onClick={run}
        disabled={loading || !!prefixError}
        className={`px-4 py-2 disabled:opacity-50 text-white rounded text-sm font-medium ${
          armed ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
        }`}
      >
        {loading ? 'Purging...' : armed ? 'Click again to confirm' : 'Purge Cache'}
      </button>

      {result && (
        <div
          className={`mt-3 p-3 rounded text-sm ${
            result.success
              ? 'bg-green-900/20 border border-green-800/50 text-green-200'
              : 'bg-red-900/20 border border-red-800/50 text-red-200'
          }`}
        >
          <p>{result.message}</p>
        </div>
      )}
    </div>
  );
}

function OpsCard({
  title,
  description,
  buttonText,
  buttonTextSecondary,
  loading,
  result,
  onClick,
  onClickSecondary,
}: {
  title: string;
  description: string;
  buttonText: string;
  buttonTextSecondary?: string;
  loading: boolean;
  result?: TriggerResult;
  onClick: () => void;
  onClickSecondary?: () => void;
}) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm text-gray-400 mt-1 mb-3">{description}</p>
      <div className="flex gap-2">
        <button
          onClick={onClick}
          disabled={loading}
          className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded text-sm font-medium"
        >
          {loading ? 'Running...' : buttonText}
        </button>
        {buttonTextSecondary && onClickSecondary && (
          <button
            onClick={onClickSecondary}
            disabled={loading}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white rounded text-sm font-medium"
          >
            {loading ? 'Running...' : buttonTextSecondary}
          </button>
        )}
      </div>
      {result && (
        <div
          className={`mt-3 p-3 rounded text-sm ${
            result.success
              ? 'bg-green-900/20 border border-green-800/50 text-green-200'
              : 'bg-red-900/20 border border-red-800/50 text-red-200'
          }`}
        >
          <p>{result.message}</p>
          {result.success && result.data != null && typeof result.data === 'object' && (
            <div className="mt-2 flex flex-wrap gap-3">
              {Object.entries(result.data as Record<string, unknown>)
                .filter(([, v]) => typeof v === 'number' || typeof v === 'boolean')
                .map(([key, val]) => (
                  <span key={key} className="text-xs">
                    <span className="text-green-400">{key}:</span> {String(val)}
                  </span>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
